import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './EnhancedTeacherSelect.css';

const EnhancedTeacherSelect = ({ 
    selectedGradeLevel, 
    selectedTeachers, 
    setSelectedTeachers, 
    onProceedToScheduling 
}) => {
    const [allTeachers, setAllTeachers] = useState([]);
    const [filteredTeachers, setFilteredTeachers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedFilter, setSelectedFilter] = useState('All');
    const [departmentFilter, setDepartmentFilter] = useState('All');
    const [gradeFilter, setGradeFilter] = useState('All');
    const [availableDepartments, setAvailableDepartments] = useState(['All']);
    const [availableGrades, setAvailableGrades] = useState(['All']);
    const [collapsedSections, setCollapsedSections] = useState({
        core: false,
        cte: false,
        both: false,
        unassigned: true // Collapse unassigned by default
    });

    // Fetch teachers for the selected grade level
    useEffect(() => {
        if (selectedGradeLevel) {
            axios.get(`/api/teachers/enhanced/?grade_level=${selectedGradeLevel}`)
                .then(response => {
                    console.log('API Response:', response);
                    console.log('API Response Data:', response.data);
                    console.log('API Response Data Type:', typeof response.data);
                    console.log('Is Array:', Array.isArray(response.data));
                    
                    // Handle different response formats
                    let teacherData = response.data;
                    if (!Array.isArray(teacherData)) {
                        console.error('API response is not an array:', teacherData);
                        if (teacherData && teacherData.results) {
                            teacherData = teacherData.results;
                        } else {
                            teacherData = [];
                        }
                    }
                    
                    const teachers = teacherData.map(teacher => ({
                        ...teacher,
                        teacher_type: teacher.teacher_type || 'Unassigned',
                        primary_departments: teacher.primary_departments || []
                    }));
                    console.log('Processed teachers:', teachers.slice(0, 3));
                    setAllTeachers(teachers);
                    
                    // Extract unique departments
                    const departments = new Set();
                    teachers.forEach(teacher => {
                        if (teacher.primary_departments && Array.isArray(teacher.primary_departments)) {
                            teacher.primary_departments.forEach(dept => {
                                if (dept && dept.trim()) {
                                    departments.add(dept.trim());
                                }
                            });
                        }
                    });
                    const sortedDepartments = Array.from(departments).sort();
                    setAvailableDepartments(['All', ...sortedDepartments]);
                    
                    // Extract unique assigned grades
                    const grades = new Set();
                    teachers.forEach(teacher => {
                        if (teacher.assigned_grades && Array.isArray(teacher.assigned_grades)) {
                            teacher.assigned_grades.forEach(grade => {
                                if (grade) {
                                    grades.add(`Grade ${grade}`);
                                }
                            });
                        } else if (!teacher.assigned_grades || teacher.assigned_grades.length === 0) {
                            grades.add('No Assignment');
                        }
                    });
                    const sortedGrades = Array.from(grades).sort();
                    setAvailableGrades(['All', ...sortedGrades]);
                    
                    // Debug log
                    console.log('Available departments:', sortedDepartments);
                    console.log('Available grades:', sortedGrades);
                })
                .catch(error => console.error('Error fetching teachers:', error));
        }
    }, [selectedGradeLevel]);

    // Filter teachers based on search and filters
    useEffect(() => {
        console.log('Filtering teachers:', {
            totalTeachers: allTeachers.length,
            searchTerm,
            selectedFilter,
            departmentFilter,
            gradeFilter
        });

        let filtered = [...allTeachers];

        // Apply search filter
        if (searchTerm && searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase().trim();
            console.log('Applying search filter:', searchLower);
            
            filtered = filtered.filter(teacher => {
                const firstName = (teacher.first_name || '').toLowerCase();
                const lastName = (teacher.last_name || '').toLowerCase();
                const fullName = `${firstName} ${lastName}`.trim();
                
                const firstNameMatch = firstName.includes(searchLower);
                const lastNameMatch = lastName.includes(searchLower);
                const fullNameMatch = fullName.includes(searchLower);
                
                let departmentMatch = false;
                if (teacher.primary_departments && Array.isArray(teacher.primary_departments)) {
                    departmentMatch = teacher.primary_departments.some(dept => 
                        dept && dept.toLowerCase().includes(searchLower)
                    );
                }
                
                const matches = firstNameMatch || lastNameMatch || fullNameMatch || departmentMatch;
                
                if (matches) {
                    console.log('Teacher matches search:', teacher.first_name, teacher.last_name);
                }
                
                return matches;
            });
            
            console.log('After search filter:', filtered.length, 'teachers');
        }

        // Apply teacher type filter
        if (selectedFilter !== 'All') {
            console.log('Applying teacher type filter:', selectedFilter);
            filtered = filtered.filter(teacher => teacher.teacher_type === selectedFilter);
            console.log('After type filter:', filtered.length, 'teachers');
        }

        // Apply department filter
        if (departmentFilter !== 'All') {
            console.log('Applying department filter:', departmentFilter);
            filtered = filtered.filter(teacher =>
                teacher.primary_departments && teacher.primary_departments.includes(departmentFilter)
            );
            console.log('After department filter:', filtered.length, 'teachers');
        }

        // Apply grade filter
        if (gradeFilter !== 'All') {
            console.log('Applying grade filter:', gradeFilter);
            filtered = filtered.filter(teacher => {
                if (gradeFilter === 'No Assignment') {
                    return !teacher.assigned_grades || teacher.assigned_grades.length === 0;
                } else {
                    const gradeNum = parseInt(gradeFilter.replace('Grade ', ''));
                    return teacher.assigned_grades && teacher.assigned_grades.includes(gradeNum);
                }
            });
            console.log('After grade filter:', filtered.length, 'teachers');
        }

        console.log('Final filtered teachers:', filtered.length);
        setFilteredTeachers(filtered);
    }, [allTeachers, searchTerm, selectedFilter, departmentFilter, gradeFilter]);

    const toggleTeacherSelection = (teacher) => {
        const isSelected = selectedTeachers.some(t => t.id === teacher.id);
        if (isSelected) {
            setSelectedTeachers(selectedTeachers.filter(t => t.id !== teacher.id));
        } else {
            setSelectedTeachers([...selectedTeachers, teacher]);
        }
    };

    const toggleSection = (section) => {
        setCollapsedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const groupTeachersByType = () => {
        const groups = {
            core: filteredTeachers.filter(t => t.teacher_type === 'Core'),
            cte: filteredTeachers.filter(t => t.teacher_type === 'CTE'),
            both: filteredTeachers.filter(t => t.teacher_type === 'Both'),
            unassigned: filteredTeachers.filter(t => t.teacher_type === 'Unassigned')
        };
        return groups;
    };

    const renderTeacherCard = (teacher) => (
        <div key={teacher.id} className="teacher-card">
            <div className="teacher-info">
                <div className="teacher-name">{teacher.first_name} {teacher.last_name}</div>
                <div className="teacher-department">
                    {teacher.primary_departments.length > 0 ? teacher.primary_departments.join(', ') : 'No Department'}
                </div>
                <div className="teacher-grades">
                    {teacher.grade_display || 'No Grade Assignment'}
                </div>
                <div className="teacher-type-badge">
                    <span className={`badge ${teacher.teacher_type.toLowerCase()}`}>
                        {teacher.teacher_type}
                    </span>
                </div>
            </div>
            <button
                className={`select-button ${selectedTeachers.some(t => t.id === teacher.id) ? 'selected' : ''}`}
                onClick={() => toggleTeacherSelection(teacher)}
            >
                {selectedTeachers.some(t => t.id === teacher.id) ? '✓' : '+'}
            </button>
        </div>
    );

    const renderTeacherGroup = (title, teachers, sectionKey, icon) => {
        if (teachers.length === 0) return null;

        const isCollapsed = collapsedSections[sectionKey];

        return (
            <div className="teacher-group">
                <div 
                    className="teacher-group-header"
                    onClick={() => toggleSection(sectionKey)}
                >
                    <span className="group-icon">{icon}</span>
                    <span className="group-title">{title} ({teachers.length})</span>
                    <span className={`collapse-icon ${isCollapsed ? 'collapsed' : ''}`}>
                        ▼
                    </span>
                </div>
                {!isCollapsed && (
                    <div className="teacher-group-content">
                        <div className="teacher-cards-grid">
                            {teachers.map(renderTeacherCard)}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const teacherGroups = groupTeachersByType();

    return (
        <div className="enhanced-teacher-select">
            <div className="teacher-select-header">
                <h3>Select Teachers for Grade {selectedGradeLevel}</h3>
                <div className="selected-count">
                    Selected: {selectedTeachers.length} teachers
                </div>
            </div>

            {/* Filters */}
            <div className="teacher-filters">
                <div className="search-box">
                    <input
                        type="text"
                        placeholder="🔍 Search teachers..."
                        value={searchTerm}
                        onChange={(e) => {
                            console.log('Search input changed:', e.target.value);
                            setSearchTerm(e.target.value);
                        }}
                        className="search-input"
                    />
                </div>

                <div className="filter-chips">
                    <div className="filter-group">
                        <label>Teacher Type:</label>
                        {['All', 'Core', 'CTE', 'Both', 'Unassigned'].map(type => (
                            <button
                                key={type}
                                className={`filter-chip ${selectedFilter === type ? 'active' : ''}`}
                                onClick={() => setSelectedFilter(type)}
                            >
                                {type}
                            </button>
                        ))}
                    </div>

                    <div className="filter-group">
                        <label>Department:</label>
                        {availableDepartments.map(dept => (
                            <button
                                key={dept}
                                className={`filter-chip ${departmentFilter === dept ? 'active' : ''}`}
                                onClick={() => setDepartmentFilter(dept)}
                            >
                                {dept}
                            </button>
                        ))}
                    </div>

                    <div className="filter-group">
                        <label>Assigned Grades:</label>
                        {availableGrades.map(grade => (
                            <button
                                key={grade}
                                className={`filter-chip ${gradeFilter === grade ? 'active' : ''}`}
                                onClick={() => setGradeFilter(grade)}
                            >
                                {grade}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Teacher Groups */}
            <div className="teacher-groups-container">
                {renderTeacherGroup('Core Teachers', teacherGroups.core, 'core', '📚')}
                {renderTeacherGroup('CTE Teachers', teacherGroups.cte, 'cte', '🔧')}
                {renderTeacherGroup('Core & CTE Teachers', teacherGroups.both, 'both', '🎯')}
                {renderTeacherGroup('Unassigned Teachers', teacherGroups.unassigned, 'unassigned', '❓')}
            </div>

            {/* Action Buttons */}
            <div className="teacher-select-actions">
                <button
                    className="proceed-button"
                    onClick={onProceedToScheduling}
                    disabled={selectedTeachers.length === 0}
                >
                    Proceed to Scheduling ({selectedTeachers.length} teachers)
                </button>
            </div>
        </div>
    );
};

export default EnhancedTeacherSelect;
